import { Action, ActionReducer, MetaReducer } from '@ngrx/store';
import { RootState } from '../../root-state';
import { TaskSharedActions } from '../task-shared.actions';
import {
  TASK_FEATURE_NAME,
  taskAdapter,
} from '../../../features/tasks/store/task.reducer';
import { Task } from '../../../features/tasks/task.model';

/**
 * Septaine F4 audit (SPEC-audit-and-refine.md): outliner indent / outdent /
 * move-with-descendants is implemented here as a meta-reducer that
 * mutates THREE entities in ONE pass:
 *
 *   1. moved task         — parentId changes
 *   2. old parent (if any) — subTaskIds loses the moved id
 *   3. new parent (if any) — subTaskIds gains the moved id at newIndex
 *
 * Single dispatch ⇒ single op in the op-log ⇒ correct replay on remote
 * devices. Three separate updateTask dispatches would emit three ops
 * that could apply out-of-order, leaving the tree corrupt (parent's
 * subTaskIds out of sync with child.parentId).
 *
 * Cycle guard: refuses to make a node its own ancestor.
 */

const isAncestor = (
  state: ReturnType<typeof taskAdapter.getInitialState>,
  candidateAncestorId: string,
  descendantId: string,
): boolean => {
  let cursor: string | undefined = descendantId;
  const visited = new Set<string>();
  while (cursor && !visited.has(cursor)) {
    visited.add(cursor);
    if (cursor === candidateAncestorId) return true;
    const t = state.entities[cursor];
    if (!t) return false;
    cursor = t.parentId;
  }
  return false;
};

const removeFromSubTaskIds = (ids: readonly string[], toRemove: string): string[] =>
  ids.filter((id) => id !== toRemove);

const insertAt = (
  ids: readonly string[],
  toInsert: string,
  index: number | undefined,
): string[] => {
  const filtered = removeFromSubTaskIds(ids, toInsert);
  if (index === undefined || index < 0 || index >= filtered.length) {
    return [...filtered, toInsert];
  }
  return [...filtered.slice(0, index), toInsert, ...filtered.slice(index)];
};

const handleOutlinerMoveNode = (
  state: RootState,
  nodeId: string,
  newParentId: string | null,
  newIndex: number | undefined,
): RootState => {
  const taskSlice = state[TASK_FEATURE_NAME];
  const movedTask = taskSlice.entities[nodeId] as Task | undefined;
  if (!movedTask) {
    return state; // task not found — no-op
  }

  // Cycle guard: can't move a node under its own descendant.
  if (
    newParentId &&
    newParentId !== movedTask.parentId &&
    isAncestor(taskSlice, nodeId, newParentId)
  ) {
    return state; // refuse — would create a cycle
  }

  const oldParentId = movedTask.parentId;
  if (oldParentId === newParentId && newIndex === undefined) {
    return state; // no-op
  }

  // Build the entity updates list.
  const updates: { id: string; changes: Partial<Task> }[] = [];

  // 1. Moved task: parentId change
  if (oldParentId !== newParentId) {
    updates.push({
      id: nodeId,
      changes: {
        parentId: newParentId === null ? undefined : newParentId,
      },
    });
  }

  // 2. Old parent: remove from subTaskIds
  if (oldParentId && oldParentId !== newParentId) {
    const oldParent = taskSlice.entities[oldParentId];
    if (oldParent) {
      updates.push({
        id: oldParentId,
        changes: {
          subTaskIds: removeFromSubTaskIds(oldParent.subTaskIds, nodeId),
        },
      });
    }
  }

  // 3. New parent: insert into subTaskIds at newIndex
  if (newParentId) {
    const newParent = taskSlice.entities[newParentId];
    if (newParent) {
      updates.push({
        id: newParentId,
        changes: {
          subTaskIds: insertAt(newParent.subTaskIds, nodeId, newIndex),
        },
      });
    }
  } else if (oldParentId === newParentId && newIndex !== undefined) {
    // Same parent, new index — only reorder.
    const sameParent =
      oldParentId && taskSlice.entities[oldParentId]
        ? taskSlice.entities[oldParentId]
        : null;
    if (sameParent) {
      updates.push({
        id: oldParentId!,
        changes: {
          subTaskIds: insertAt(sameParent.subTaskIds, nodeId, newIndex),
        },
      });
    }
  }

  if (updates.length === 0) {
    return state;
  }

  return {
    ...state,
    [TASK_FEATURE_NAME]: taskAdapter.updateMany(updates, taskSlice),
  };
};

/**
 * Meta-reducer that intercepts outliner-specific multi-entity actions.
 * Registered alongside taskSharedSchedulingMetaReducer in
 * meta-reducer-registry.ts.
 */
export const taskSharedOutlinerMoveMetaReducer: MetaReducer = (
  reducer: ActionReducer<RootState, Action>,
) => {
  return (state: unknown, action: Action) => {
    if (!state) return reducer(state as RootState | undefined, action);
    const rootState = state as RootState;

    if (action.type === TaskSharedActions.outlinerMoveNode.type) {
      const { nodeId, newParentId, newIndex } = action as ReturnType<
        typeof TaskSharedActions.outlinerMoveNode
      >;
      const updatedState = handleOutlinerMoveNode(
        rootState,
        nodeId,
        newParentId,
        newIndex,
      );
      return reducer(updatedState, action);
    }

    return reducer(rootState, action);
  };
};
